import React, { useMemo, useState } from 'react';
import type { Item } from '../../../../types/orders';

interface OrcObservationsSectionProps {
  orderObservation?: string | null;
  items: Item[];
}

interface ItemObservation {
  item: Item;
  text: string;
}

function normalizeObservation(value?: string | null): string | null {
  const normalized = value?.trim();
  return normalized || null;
}

export const OrcObservationsSection: React.FC<OrcObservationsSectionProps> = ({
  orderObservation,
  items,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const normalizedOrderObservation = normalizeObservation(orderObservation);
  const itemObservations = useMemo<ItemObservation[]>(() => {
    const result: ItemObservation[] = [];

    for (const item of items) {
      const text = normalizeObservation(item.observacao);
      if (text) {
        result.push({ item, text });
      }
    }

    return result;
  }, [items]);

  if (!normalizedOrderObservation && itemObservations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2" data-testid="orc-observations-section">
      <button
        type="button"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-amber-400/30 bg-amber-500/10 px-2 py-1.5 text-[11px] font-medium text-amber-200 transition-colors hover:bg-amber-500/20"
        aria-expanded={isExpanded}
      >
        <span>{isExpanded ? 'Ocultar observações' : 'Ver observações'}</span>
        <span className="text-amber-300/70">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className="space-y-3 rounded-lg border border-navy-700 bg-navy-900/20 p-3">
          {normalizedOrderObservation && (
            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-300">
                Pedido
              </div>
              <p className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-gray-200">
                {normalizedOrderObservation}
              </p>
            </div>
          )}

          {itemObservations.length > 0 && (
            <div className="space-y-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-green-300">
                Itens
              </div>
              {itemObservations.map(({ item, text }) => (
                <div
                  key={`item-observation-${item.oit_id}`}
                  className="space-y-1 border-t border-navy-700 pt-2 first:border-t-0 first:pt-0"
                >
                  <div className="text-[11px] font-medium text-gray-200">
                    {`I-${item.oit_id} · ${item.produto?.nome ?? 'Produto'}`}
                  </div>
                  <p className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-gray-300">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
